import React, { useEffect, useContext, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { navigate, RouteComponentProps } from '@reach/router';
import { Box, LinearProgress, MenuItem, Select, SelectChangeEvent } from '@mui/material';

import {
  CommonCard,
  Header,
  BillingTable,
  CompanyDetails,
  AccountEngagementHeader,
  AccountSettingsDrawer
} from './particles';

import { ApplicationContext, TableContext } from '@particles';
import {
  callGetCompanyUsers,
  callGetLicenseLatest,
  callGetTotalUsers,
  callGetUser,
  callDeleteLicense,
  callGetTeamsList,
  useCompanyBillingModel,
  useCompanyDetails
} from '@api';
import { TextView, Button, AlertPopUp, Alert } from '@atoms';
import {
  EditAccountModal,
  EditCompanyModal,
  TeamTable,
  CompanyTeam
} from '@organisms';
// Direct imports — the @pages barrel re-exports BillingDetails from this
// same file, so going through it creates a circular dependency that only
// surfaces once routes are code-split via React.lazy (the eager-import
// version masked it by resolving everything before any module was used).
import AddUserModal from '../../pages/admin/team/particles/addUserModal';
import AddCompanyTeam from '../addCompanyTeam';
import UploadUserModal from '../../pages/admin/team/particles/uploadUserModal';
import { toast } from 'react-toastify';

interface BillingDetailsProps extends RouteComponentProps {
  id?: string;
  type: 'superadmin' | 'admin';
}

const BillingDetails: React.FC<BillingDetailsProps> = (props: BillingDetailsProps) => {
  const { state } = useContext(ApplicationContext);
  const { dispatch: dispatchTable } = useContext(TableContext);
  const { type, id } = props;
  const isSuperAdmin = type === 'superadmin';
  const companyId = isSuperAdmin ? id : state.user?.companyId;
  // Read here rather than reported up by BillingModelPanel: the panel now lives
  // in the settings drawer and is unmounted while it is closed. Same query key,
  // so a change made in the drawer re-renders this page too.
  const { data: billingModel = 'license' } = useCompanyBillingModel(
    isSuperAdmin ? companyId : undefined
  );
  const [openSettings, setOpenSettings] = useState<boolean>(false);
  // Read against the company itself, so a company with no users yet still
  // shows its details and can be edited.
  const companyDetailsQuery = useCompanyDetails(isSuperAdmin ? companyId : undefined);
  const [openEditCompany, setEditCompany] = useState<boolean>(false);

  const [selectedTeamForFilter, setSelectedTeamForFilter] = useState<Team>();
  const [openAddUser, setOpenAddUser] = useState<boolean>(false);
  const [openAddTeam, setOpenAddTeam] = useState<boolean>(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent>();
  const [selectedTeam, setSelectedTeam] = useState<Team>();
  const [user, setUser] = useState<User>();
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [openEdit, setEdit] = useState<boolean>(false);
  const [openDelete, setDelete] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<number>(0);
  const [alert, setAlert] = useState<
    { title: string; message: string; type: 'success' | 'error' } | undefined
  >(undefined);

  const [openUploadUser, setOpenUploadUser] = useState<boolean>(false);

  const licenseQuery = useQuery({
    queryKey: ['billing/license-latest', companyId],
    queryFn: () => callGetLicenseLatest({ companyId: companyId! }),
    enabled: !!companyId
  });

  const totalUsersQuery = useQuery({
    queryKey: ['billing/total-users', companyId],
    queryFn: () => callGetTotalUsers({ companyId }),
    enabled: !!companyId
  });

  const companyUsersQuery = useQuery({
    queryKey: ['billing/company-users-first', companyId],
    queryFn: () => callGetCompanyUsers({ companyId: companyId!, pageNumber: 1, pageSize: 1 }),
    enabled: !!companyId
  });

  const firstAgentEmail =
    companyUsersQuery.data?.code === 200 &&
    companyUsersQuery.data.data &&
    companyUsersQuery.data.data.totalCount > 0
      ? companyUsersQuery.data.data.data[0]?.email
      : undefined;

  const userQuery = useQuery({
    queryKey: ['billing/user', companyId, firstAgentEmail],
    queryFn: () => callGetUser({ companyId: companyId!, email: firstAgentEmail! }),
    enabled: !!companyId && !!firstAgentEmail
  });

  const teamsQuery = useQuery({
    queryKey: ['billing/teams', companyId, refresh],
    queryFn: () => callGetTeamsList({ companyId: companyId! }),
    enabled: !!companyId
  });

  const licensePayload = licenseQuery.data?.code === 200 ? licenseQuery.data.data : undefined;
  const total = licensePayload
    ? (licensePayload as { other: { licensed: number } }).other.licensed
    : 0;
  const license: License | undefined =
    licensePayload && licensePayload.data && licensePayload.data.length > 0
      ? licensePayload.data[0]
      : undefined;
  const activeUser = totalUsersQuery.data?.code === 200
    ? totalUsersQuery.data.data?.data?.total?.active ?? 0
    : 0;
  const teamList: Team[] = teamsQuery.data?.data ?? [];

  // Mirror userQuery → local user state so existing EditAccountModal onSuccess
  // (which sets the local user) keeps working.
  useEffect(() => {
    if (userQuery.data?.code === 200) {
      setUser(userQuery.data.data);
    }
  }, [userQuery.data]);

  const loading =
    deleteLoading ||
    licenseQuery.isFetching ||
    totalUsersQuery.isFetching ||
    companyUsersQuery.isFetching ||
    userQuery.isFetching ||
    teamsQuery.isFetching;

  const apiDeleteAccount = async () => {
    if (!companyId) return;
    setDeleteLoading(true);
    try {
      const res = await callDeleteLicense(companyId);
      if (res.code === 200) {
        navigate('/allaccount');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    if (!companyId) {
      setAlert({
        title: 'CompanyId missing',
        message: 'contact your admin person',
        type: 'error'
      });
      const t = setTimeout(() => setAlert(undefined), 3000);
      return () => clearTimeout(t);
    }
    return () => {
      dispatchTable({
        type: `set`,
        key: `teamPagination`,
        value: { page: 1, perPage: 10 }
      });
      dispatchTable({
        type: `set`,
        key: `billingPagination`,
        value: { page: 1, perPage: 10 }
      });
    };
  }, [companyId, dispatchTable]);

  // Bumping `refresh` invalidates teamsQuery (it depends on it) and propagates
  // to the TeamTable/CompanyTeam children that re-fetch when this number changes.
  const bumpRefresh = () => setRefresh(Date.now());

  if (!companyId) {
    return (
      <Box sx={{ m: 2 }}>
        <TextView>CompanyID is missing</TextView>
      </Box>
    );
  }

  const handleChangeTeamSelected = (event: SelectChangeEvent) => {
    const _teamData = teamList.find((item) => item.id === event.target.value);
    setSelectedTeamForFilter(_teamData);
  };

  return (
    <Box sx={{ m: 2 }}>
      {loading && <LinearProgress />}
      <Box sx={{ mt: 1, mb: 3 }}>
        <Header
          isSuerAdmin={isSuperAdmin}
          onPressUpdate={() => {
            // With a first user, keep editing user + company together. Without
            // one there is nothing to save against, so edit the company alone.
            if (user) setEdit(true);
            else setEditCompany(true);
          }}
          onPressDelete={() => {
            setDelete(true);
          }}
          onPressSettings={() => setOpenSettings(true)}
        />
        {isSuperAdmin && (
          <AccountSettingsDrawer
            open={openSettings}
            onClose={() => setOpenSettings(false)}
            companyId={companyId}
            onSynced={() => bumpRefresh()}
          />
        )}
      </Box>
      {!loading && (
        <Box>
          <Box sx={{ mb: 3 }}>{isSuperAdmin && <CompanyDetails user={user} company={companyDetailsQuery.data} />}</Box>
          {/* A consumption-only agency has no seat count and no expiry, so the
              licence summary and its history would show a limit that nothing
              enforces. */}
          {billingModel !== 'token' && (
            <CommonCard licensed={total} active={activeUser} license={license} />
          )}

          {isSuperAdmin && (
            <Box mt={3}>
              <AccountEngagementHeader
                title={'Account Engagement'}
                teamList={teamList}
                companyId={companyId}
              />
            </Box>
          )}

          {billingModel !== 'token' && (
            <BillingTable
              isSuperAdmin={isSuperAdmin}
              licensedCount={total}
              activeCount={activeUser}
              license={license}
              companyId={companyId}
            />
          )}

          <Box sx={{ flexDirection: 'row', display: 'flex', alignItems: 'center', mt: 2 }}>
            <TextView display={'initial'} $fontSize={30}>
              All users
            </TextView>
            <Box sx={{ flex: 1 }} />
            <TextView $fontWeight={400} $fontSize={14} mr={1}>
              Select Team
            </TextView>
            <Select
              value={selectedTeamForFilter?.id ?? '0'}
              onChange={handleChangeTeamSelected}
              displayEmpty
              size={'small'}
              sx={{ backgroundColor: '#ffffff', fontSize: 14, mr: 1 }}
              inputProps={{ 'aria-label': 'Without label' }}
            >
              <MenuItem key={0} value={'0'}>
                {'All Team'}
              </MenuItem>
              {teamList.map((item) => {
                return (
                  <MenuItem key={item.id} value={`${item.id}`}>
                    {item.teamName}
                  </MenuItem>
                );
              })}
            </Select>
            <Button onClick={() => setOpenAddUser(true)}>{'Add User'}</Button>
            <Button onClick={() => setOpenUploadUser(true)} sx={{ ml: 1 }}>
              Upload Users
            </Button>

            <UploadUserModal
              companyId={companyId}
              open={openUploadUser}
              onClose={() => setOpenUploadUser(false)}
              onSuccess={() => {
                bumpRefresh();
                toast.success('User invited successfully');
                setOpenUploadUser(false);
              }}
            />
          </Box>

          <TeamTable
            companyId={companyId}
            refresh={refresh}
            user={user}
            isSuperAdmin={isSuperAdmin}
            selectedTeam={selectedTeamForFilter}
            onEdit={(agent: Agent) => {
              setSelectedAgent(agent);
              setOpenAddUser(true);
            }}
          />

          <EditCompanyModal
            companyId={companyId}
            open={openEditCompany}
            initial={companyDetailsQuery.data}
            onClose={() => setEditCompany(false)}
          />

          <EditAccountModal
            user={user}
            open={openEdit}
            onClose={() => setEdit(false)}
            onSuccess={(user) => {
              setEdit(false);
              setUser(user);
            }}
          />

          <AddUserModal
            open={openAddUser}
            selectedAgent={selectedAgent}
            companyId={companyId}
            onClose={() => {
              setOpenAddUser(false);
              setSelectedAgent(undefined);
              bumpRefresh();
            }}
            onSuccess={() => {
              setOpenAddUser(false);
              setSelectedAgent(undefined);
              bumpRefresh();
            }}
          />

          <AlertPopUp
            open={openDelete}
            title={'Delete'}
            message={'Are you sure, you want to delete account?'}
            onSuccess={() => {
              setDelete(false);
              apiDeleteAccount();
            }}
            onClose={() => {
              setDelete(false);
            }}
          />

          {/* START:Team Block*/}

          <Box sx={{ flexDirection: 'row', display: 'flex', alignItems: 'center', mt: 2 }}>
            <TextView display={'initial'} $fontSize={30}>
              All Teams
            </TextView>
            <Box sx={{ flex: 1 }} />
            <Button onClick={() => setOpenAddTeam(true)}>{'Add Team'}</Button>
          </Box>

          <CompanyTeam
            companyId={companyId}
            refresh={refresh}
            isSuperAdmin={isSuperAdmin}
            onEdit={(team: Team) => {
              setSelectedTeam(team);
              setOpenAddTeam(true);
            }}
          />

          <AddCompanyTeam
            open={openAddTeam}
            selectedTeam={selectedTeam}
            companyId={companyId}
            onClose={() => {
              setOpenAddTeam(false);
              setSelectedTeam(undefined);
            }}
            onSuccess={() => {
              setOpenAddTeam(false);
              setSelectedTeam(undefined);
              bumpRefresh();
            }}
          />

          {alert && <Alert severity={alert.type} title={alert.title} message={alert.message} />}

          {/* END: TEAM BLOCK */}
        </Box>
      )}
    </Box>
  );
};

export default BillingDetails;
